import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { usePagesStore } from '../../stores/pagesStore';
import { useDialogStore } from '../../stores/dialogStore';
import ParagraphEditor from '../../components/ParagraphEditor';

const Pages = () => {
  const { accessToken, user } = useAuthStore();
  const {
    pages,
    currentPage,
    revisions,
    fetchPages,
    createPage,
    fetchPage,
    fetchRevisions,
    publishRevision,
    createDraft,
    discardDraft,
    updatePage,
    deletePage,
    isLoading,
    error,
    setCurrentPage
  } = usePagesStore();
  const { alert, confirm } = useDialogStore();

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRevisionsModal, setShowRevisionsModal] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPageId, setEditingPageId] = useState(null);
  const [editingRevisionId, setEditingRevisionId] = useState(null);
  const [newPageTitle, setNewPageTitle] = useState('');
  const [newPageVisibility, setNewPageVisibility] = useState(2); // MEMBERS by default

  // Check if user is admin
  if (!user || user.is_admin !== 1) {
    return (
      <div className="surface rounded-lg p-8 text-center">
        <h2 className="text-2xl font-bold text-primary mb-4">Access Denied</h2>
        <p className="text-secondary">You must be an administrator to access this page.</p>
      </div>
    );
  }

  useEffect(() => {
    if (accessToken) {
      fetchPages(accessToken, searchTerm || null, statusFilter || null);
    }
  }, [accessToken, searchTerm, statusFilter, fetchPages]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleCreatePage = async () => {
    if (!newPageTitle.trim()) {
      alert('Error', 'Page title is required');
      return;
    }

    const result = await createPage({
      title: newPageTitle,
      visibility_id: newPageVisibility
    }, accessToken);

    if (result.success) {
      setShowCreateModal(false);
      setNewPageTitle('');
      setNewPageVisibility(2);
      alert('Success', 'Page created successfully');
      // Refresh pages list
      fetchPages(accessToken);
    } else {
      alert('Error', result.error || 'Failed to create page');
    }
  };

  const handlePageClick = async (page) => {
    setEditingPageId(page.id);

    // If there's a draft, edit the draft
    if (page.has_draft && page.draft_revision_id) {
      setEditingRevisionId(page.draft_revision_id);
      setShowEditor(true);
    } else if (page.published_revision_id) {
      // No draft exists - create one from published
      const confirmed = await confirm(
        'Create Draft',
        'This will create a working copy of the published page. Continue?'
      );

      if (!confirmed) {
        setEditingPageId(null);
        return;
      }

      const result = await createDraft(page.id, accessToken);
      if (result.success) {
        setEditingRevisionId(result.revisionId);
        setShowEditor(true);
        // Refresh pages list to show draft badge
        fetchPages(accessToken, searchTerm || null, statusFilter || null);
      } else {
        alert('Error', result.error || 'Failed to create draft');
        setEditingPageId(null);
      }
    } else {
      alert('Error', 'No published revision found for this page');
      setEditingPageId(null);
    }
  };

  const handleCloseEditor = () => {
    setShowEditor(false);
    setEditingPageId(null);
    setEditingRevisionId(null);
    // Refresh pages list in case content was updated
    fetchPages(accessToken, searchTerm || null, statusFilter || null);
  };

  const handlePublishDraft = async () => {
    if (!editingPageId || !editingRevisionId) return;

    const confirmed = await confirm(
      'Publish Draft',
      'This will publish the draft and archive the current published version. The archived version\'s media will be deleted. Continue?'
    );

    if (!confirmed) return;

    const result = await publishRevision(editingPageId, editingRevisionId, accessToken);
    if (result.success) {
      alert('Success', 'Draft published successfully');
      handleCloseEditor();
    } else {
      alert('Error', result.error || 'Failed to publish draft');
    }
  };

  const handleDiscardDraft = async () => {
    if (!editingPageId || !editingRevisionId) return;

    const confirmed = await confirm(
      'Discard Draft',
      'This will delete the draft and all its media. This cannot be undone. Continue?'
    );

    if (!confirmed) return;

    const result = await discardDraft(editingPageId, editingRevisionId, accessToken);
    if (result.success) {
      alert('Success', 'Draft discarded successfully');
      handleCloseEditor();
    } else {
      alert('Error', result.error || 'Failed to discard draft');
    }
  };

  const handleViewRevisions = async (page) => {
    setCurrentPage(page);
    await fetchRevisions(page.id, accessToken);
    setShowRevisionsModal(true);
  };

  const handlePublishRevision = async (revisionId) => {
    const confirmed = await confirm(
      'Publish Revision',
      'Are you sure you want to publish this revision? It will replace the current published version.'
    );

    if (!confirmed) return;

    const result = await publishRevision(currentPage.id, revisionId, accessToken);

    if (result.success) {
      alert('Success', 'Revision published successfully');
      // Refresh revisions
      await fetchRevisions(currentPage.id, accessToken);
      // Refresh pages list
      await fetchPages(accessToken);
    } else {
      alert('Error', result.error || 'Failed to publish revision');
    }
  };

  const handleVisibilityChange = async (page, newVisibility) => {
    const result = await updatePage(page.id, { visibility_id: newVisibility }, accessToken);

    if (result.success) {
      // Refresh pages list
      await fetchPages(accessToken);
    } else {
      alert('Error', result.error || 'Failed to update visibility');
    }
  };

  const handleDeletePage = async (page) => {
    const confirmed = await confirm(
      'Delete Page',
      `This will permanently delete the page "${page.title}" and all its content, media, and revisions. This cannot be undone. Continue?`
    );

    if (!confirmed) return;

    const result = await deletePage(page.id, accessToken);

    if (result.success) {
      alert('Success', 'Page deleted successfully');
      // Pages list is already updated by the store
    } else {
      alert('Error', result.error || 'Failed to delete page');
    }
  };

  if (isLoading && pages.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error && pages.length === 0) {
    return (
      <div className="surface rounded-lg p-6">
        <p className="text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  // Show editor if a page is being edited
  if (showEditor && editingPageId && editingRevisionId) {
    return (
      <div className="surface rounded-lg shadow-lg p-6">
        <ParagraphEditor
          pageId={editingPageId}
          revisionId={editingRevisionId}
          onClose={handleCloseEditor}
          onPublish={handlePublishDraft}
          onDiscard={handleDiscardDraft}
          isDraft={true}
        />
      </div>
    );
  }

  return (
    <div className="surface rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-primary">CMS Pages</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors"
        >
          + Create Page
        </button>
      </div>

      {/* Search and Filter */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search pages by title..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
          />
        </div>
        <div className="w-48">
          <select
            value={statusFilter}
            onChange={handleStatusChange}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
          >
            <option value="">All Status</option>
            <option value="DRAFT">Draft</option>
            <option value="PUBLISHED">Published</option>
            <option value="ARCHIVED">Archived</option>
          </select>
        </div>
      </div>

      {/* Pages List */}
      <div className="space-y-3">
        {pages.map((page) => (
          <div
            key={page.id}
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:border-primary-400 dark:hover:border-primary-600 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 cursor-pointer" onClick={() => handlePageClick(page)}>
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-primary">{page.title || 'Untitled'}</h3>
                  <span className="text-sm text-gray-500 dark:text-gray-400 font-mono">/{page.slug}</span>
                  {page.status_label && (
                    <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
                      PUBLISHED
                    </span>
                  )}
                  {page.has_draft && (
                    <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300">
                      DRAFT
                    </span>
                  )}
                  {page.visibility_label && (
                    <span className="px-2 py-1 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">
                      {page.visibility_label}
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Created: {new Date(page.created_at).toLocaleDateString()}
                  {page.updated_at && ` • Updated: ${new Date(page.updated_at).toLocaleDateString()}`}
                </div>
              </div>

              <div className="flex items-center gap-2 ml-4">
                {/* Visibility Selector */}
                <select
                  value={page.visibility_id || 2}
                  onChange={(e) => handleVisibilityChange(page, parseInt(e.target.value))}
                  onClick={(e) => e.stopPropagation()}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
                >
                  <option value="1">Private</option>
                  <option value="2">Members</option>
                  <option value="3">Public</option>
                </select>

                {/* View Revisions Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewRevisions(page);
                  }}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded text-primary hover:bg-gray-100 dark:hover:bg-gray-700"
                  title="View Revisions"
                >
                  Revisions
                </button>

                {/* Edit Button */}
                <button
                  onClick={() => handlePageClick(page)}
                  className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                  title="Edit Page"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>

                {/* Delete Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeletePage(page);
                  }}
                  className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                  title="Delete Page"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {pages.length === 0 && !isLoading && (
        <p className="text-center text-secondary py-8">No pages found</p>
      )}

      {/* Create Page Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-primary mb-4">Create New Page</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary mb-1">
                  Page Title *
                </label>
                <input
                  type="text"
                  value={newPageTitle}
                  onChange={(e) => setNewPageTitle(e.target.value)}
                  placeholder="Enter page title"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
                  maxLength={160}
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-1">Slug will be auto-generated from title</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary mb-1">
                  Visibility
                </label>
                <select
                  value={newPageVisibility}
                  onChange={(e) => setNewPageVisibility(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-primary"
                >
                  <option value="1">Private</option>
                  <option value="2">Members</option>
                  <option value="3">Public</option>
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleCreatePage}
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
              >
                Create
              </button>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewPageTitle('');
                  setNewPageVisibility(2);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded text-secondary hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revisions Modal */}
      {showRevisionsModal && currentPage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-3xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-primary">
                Revision History: {currentPage.slug}
              </h3>
              <button
                onClick={() => setShowRevisionsModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-3">
              {revisions.map((revision) => (
                <div
                  key={revision.id}
                  className={`p-4 rounded-lg border ${
                    revision.id === currentPage.published_revision_id
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-primary">{revision.title}</h4>
                        <span className="text-sm text-gray-500 dark:text-gray-400">v{revision.version}</span>
                        <span className={`px-2 py-1 text-xs font-medium rounded ${
                          revision.status_code === 'PUBLISHED'
                            ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                            : revision.status_code === 'DRAFT'
                            ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300'
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                        }`}>
                          {revision.status_label}
                        </span>
                        {revision.id === currentPage.published_revision_id && (
                          <span className="px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
                            Current Published
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {revision.submitted_by && `By: ${revision.submitted_by} • `}
                        Created: {new Date(revision.created_at).toLocaleString()}
                        {revision.published_at && ` • Published: ${new Date(revision.published_at).toLocaleString()}`}
                      </div>
                    </div>

                    {revision.status_code !== 'PUBLISHED' && (
                      <button
                        onClick={() => handlePublishRevision(revision.id)}
                        className="ml-4 px-3 py-1 text-sm bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Publish
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {revisions.length === 0 && (
              <p className="text-center text-secondary py-8">No revisions found</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Pages;
